import { ThemeSwitch } from '@/business-components/theme-switch/ThemeSwitch';
import { VersionSelect } from '@/business-components/version-select/VersionSelect';
import { CustomLink } from '@/components/custom-link/CustomLink';
import { useVersionListAndSelectedName } from '@/hooks/useVersionListAndSelectedName';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { DOCS_SITE_EVENT } from '@/shared/constant/Analytics';
import type { NavRightLinkItem } from '@/shared/constant/navigation/NavRightLinkItem';
import { NavRightLinkItemTypeEnum } from '@/shared/constant/navigation/NavRightLinkItem';
import { gaReport } from '@/shared/util/analytics';
import { NavRightButtonStyleOptions } from '@tarslib/types/lib/publishedProject';
import type { ButtonProps } from 'aui';
import { Button } from 'aui';
import clsx from 'clsx';
import React from 'react';

export type NavRightLinkContentProps = {
  value: NavRightLinkItem[];
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onVersionSelectClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  className?: string;
};

export function NavRightLinkContent(props: NavRightLinkContentProps) {
  const { value, className, onClick, onVersionSelectClick } = props;
  const title = useDocsDataModelStore((x) => x.docsBaseConfig.title);
  const displayThemeToggle = useDocsDataModelStore((x) => x.docsBaseConfig.displayThemeToggle);
  const versionList = useVersionListAndSelectedName()?.versionList;

  const isHidden = React.useMemo(() => {
    return !value.length && !displayThemeToggle && !versionList?.length;
  }, [value, displayThemeToggle, versionList]);

  return (
    <div
      className={clsx(
        'flex flex-none flex-shrink-0 items-center gap-1',
        {
          ['hidden']: isHidden,
        },
        className,
      )}
    >
      {value.map((x) => {
        return (
          <CustomLink key={x.key} to={x.to} target={x.target} onClick={onClick}>
            <div
              className={clsx('relative overflow-hidden', {
                ['rounded-lg p-1.5 hover:bg-btn-text-hover']:
                  x.type !== NavRightLinkItemTypeEnum.Button,
              })}
            >
              {x.type === NavRightLinkItemTypeEnum.Icon && (
                <img
                  className={clsx('relative block h-5 rounded', {
                    ['left-[-80px] drop-shadow-[var(--ui-text-color)_80px_0]']: String(x.icon)
                      .toLowerCase()
                      .endsWith('.svg'),
                  })}
                  src={x.icon}
                  alt={x.title}
                  title={x.title}
                />
              )}
              {x.type === NavRightLinkItemTypeEnum.Text && (
                <span className="leading-5">{x.name}</span>
              )}

              {x.type === NavRightLinkItemTypeEnum.Button && (
                <Button
                  {...(() => {
                    const map: Record<NavRightButtonStyleOptions, ButtonProps> = {
                      [NavRightButtonStyleOptions.RECTANGULAR_FILL]: {
                        type: 'primary',
                      },
                      [NavRightButtonStyleOptions.RADIUS_FILL]: {
                        shape: 'round',
                        type: 'primary',
                      },
                      [NavRightButtonStyleOptions.RECTANGULAR_STROKE]: {
                        ghost: true,
                        type: 'primary',
                      },
                      [NavRightButtonStyleOptions.RADIUS_STROKE]: {
                        ghost: true,
                        shape: 'round',
                        type: 'primary',
                      },
                      [NavRightButtonStyleOptions.SINGLE_TEXT]: {
                        type: 'link',
                      },
                    };
                    return map[x.style];
                  })()}
                  onClick={() => {
                    gaReport(DOCS_SITE_EVENT.NAVIGATION_RIGHT_FUNCTION_AREA, {
                      title: x.name,
                      page_title: title,
                    });
                  }}
                  className="h-7 py-0.5"
                >
                  {x.name}
                </Button>
              )}
            </div>
          </CustomLink>
        );
      })}

      <VersionSelect onClick={onVersionSelectClick} />
      <ThemeSwitch />
    </div>
  );
}
